import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import ScienceIcon from "@mui/icons-material/ScienceRounded";

import Card3D from "./Card3D";
import { api, money, tone } from "../api";

interface LogRow {
  page: string;
  label: string;
  path: string;
  ts: number;
  from: string | null;
  to: string | null;
  timeframe: string | null;
  trades: number;
  net: number;
  costs: number;
  profitable: number | null;
  total: number | null;
  win_rate: number | null;
  best: { name: string; net: number; trades: number; profit_factor: number | null } | null;
  params: Record<string, unknown>;
}

function ago(ts: number): string {
  const m = Math.round((Date.now() / 1000 - ts) / 60);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * What every strategy last scored, in one place.
 *
 * Each strategy page runs its own backtest and forgets it on reload. The
 * backend keeps the latest result per page so this panel can answer "how is
 * each strategy doing" without visiting seven pages and re-running seven
 * backtests.
 */
export default function BacktestLog() {
  const [rows, setRows] = useState<LogRow[] | null>(null);

  useEffect(() => {
    const load = () => void api.backtestLog().then((d) => setRows(d.results)).catch(() => {});
    load();
    const id = window.setInterval(load, 20000);
    return () => window.clearInterval(id);
  }, []);

  if (!rows) return null;

  if (rows.length === 0) {
    return (
      <Card3D intensity={1}>
        <Stack direction="row" alignItems="center" gap={1.4} sx={{ p: 2.2 }}>
          <ScienceIcon sx={{ fontSize: 20, color: "var(--muted)" }} />
          <Box>
            <Typography sx={{ fontSize: ".88rem", fontWeight: 800 }}>
              Backtest Results
            </Typography>
            <Typography sx={{ fontSize: ".7rem", color: "var(--muted)", mt: 0.2 }}>
              Nothing run yet. Open any strategy page, press Run, and the result
              lands here — it survives reloads and server restarts.
            </Typography>
          </Box>
        </Stack>
      </Card3D>
    );
  }

  const net = rows.reduce((a, r) => a + (r.net ?? 0), 0);

  return (
    <Card3D intensity={1}>
      <Box sx={{ p: 2.2 }}>
        <Stack direction="row" alignItems="center" gap={1.2} sx={{ mb: 1.4 }}>
          <ScienceIcon sx={{ fontSize: 19, color: "var(--purple)" }} />
          <Box>
            <Typography sx={{ fontSize: ".9rem", fontWeight: 800 }}>
              Backtest Results
            </Typography>
            <Typography sx={{ fontSize: ".64rem", color: "var(--muted)", mt: 0.2 }}>
              The latest run from each strategy page. Historical simulation —
              not live money.
            </Typography>
          </Box>
          <Box sx={{ ml: "auto", textAlign: "right" }}>
            <Typography sx={{
              fontSize: ".55rem", fontWeight: 800, letterSpacing: ".07em",
              textTransform: "uppercase", color: "var(--muted)",
            }}>
              Combined
            </Typography>
            <Typography className={`mono ${tone(net)}`} sx={{ fontSize: ".95rem", fontWeight: 800 }}>
              {money(net, true)}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", minWidth: 800, borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr">
                {["Strategy", "Window", "TF", "Trades", "Net P&L", "Costs", "Win%", "Profitable", "Best performer", "Run"].map((h) => (
                  <Box key={h} component="th" sx={{
                    textAlign: ["Strategy", "Window", "TF", "Best performer"].includes(h) ? "left" : "right",
                    fontSize: ".55rem", fontWeight: 800, letterSpacing: ".06em",
                    textTransform: "uppercase", color: "var(--muted)",
                    py: 0.8, px: 0.7, borderBottom: "1px solid var(--border)",
                    whiteSpace: "nowrap",
                  }}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((r) => (
                <Box component="tr" key={r.page} sx={{ "&:hover": { background: "#0e1622" } }}>
                  <Box component="td" sx={{ ...cell, fontWeight: 700 }}>
                    <Box component={Link} to={r.path}
                      sx={{ color: "var(--text)", textDecoration: "none", "&:hover": { color: "var(--blue)" } }}>
                      {r.label}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ ...cell, color: "var(--muted)", fontSize: ".66rem" }}>
                    {r.from && r.to ? `${r.from} → ${r.to}` : "—"}
                  </Box>
                  <Box component="td" sx={{ ...cell, color: "var(--muted)", fontSize: ".66rem" }}>
                    {r.timeframe ?? "—"}
                  </Box>
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right" }}>
                    {r.trades?.toLocaleString() ?? "—"}
                  </Box>
                  <Box component="td" className={`mono ${tone(r.net)}`} sx={{ ...cell, textAlign: "right", fontWeight: 800 }}>
                    {money(r.net, true)}
                  </Box>
                  <Box component="td" className="mono down" sx={{ ...cell, textAlign: "right", fontSize: ".7rem" }}>
                    {r.costs ? money(r.costs) : "—"}
                  </Box>
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right" }}>
                    {r.win_rate ?? "—"}
                  </Box>
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right" }}>
                    {r.profitable != null && r.total != null ? `${r.profitable}/${r.total}` : "—"}
                  </Box>
                  <Box component="td" sx={{ ...cell, fontSize: ".66rem" }}>
                    {r.best ? (
                      <Tooltip title={`${r.best.trades} trades${r.best.profit_factor ? `, PF ${r.best.profit_factor}` : ""}`}>
                        <Stack direction="row" gap={0.7} alignItems="baseline">
                          <span style={{ color: "var(--muted)" }}>{r.best.name}</span>
                          <span className={`mono ${tone(r.best.net)}`} style={{ fontWeight: 800 }}>
                            {money(r.best.net, true)}
                          </span>
                        </Stack>
                      </Tooltip>
                    ) : <span style={{ color: "var(--muted)" }}>—</span>}
                  </Box>
                  <Box component="td" sx={{ ...cell, textAlign: "right", fontSize: ".62rem", color: "var(--muted)" }}>
                    {ago(r.ts)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Card3D>
  );
}

const cell = {
  py: 0.8, px: 0.7, borderBottom: "1px solid var(--border)",
  fontSize: ".76rem", whiteSpace: "nowrap" as const,
};
